<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceLines',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    currency: {
      type: Object,
      required: true,
    },
    defaultDiscount: {
      type: Number,
      required: false,
    },
    defaultTaxCode: {
      type: String,
      required: false,
    },
    formContext: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onProductSelected = (...args) => {
    let [row, product] = args;

    invoiceLineHelper.value.onProductSelected(row.data, product);
    row.setFocus('quantity');
  };
  const hasQuantityMismatch = (...args) => {
    const row = args[0];
    return (
      row.data.accountings &&
      row.data.accountings.data &&
      row.data.accountings.data.length &&
      _.sumBy(row.data.accountings.data, (p) => p.quantity) != row.data.quantity
    );
  };
  const getInvoiceLineEditorIsDisabled = (...args) => {
    const [row] = args;
    return !row.tax_code;
  };
  const onRentalOrPropertySelected = (...args) => {
    let [row, selection] = args;

    console.log(row, selection);

    if (!selection) {
      row.data.rental = null;
      row.data.property = null;
      return;
    }

    if (_.isObject(selection) && selection.object == 'rental') {
      row.data.rental = selection;
      row.data.property = null;
      return;
    }

    if (
      _.isObject(selection) &&
      ['property', 'property_unit'].indexOf(selection.object) > -1
    ) {
      row.data.rental = null;
      row.data.property = selection;
      return;
    }
  };

  const expenseTypes = ref(null);
  const taxCodes = ref(null);
  const currencies = ref(null);
  const units = ref(null);
  const displayAddInvoiceLine = ref(null);
  const messages = ref({
    invoiceLineProductSearchPlaceholder: $fn.value.pgettext(
      'InvoiceLines',
      'Search product/service...'
    ),
    invoiceLineCostCenterSearchPlaceholder: $fn.value.pgettext(
      'invoiceLines',
      'search...'
    ),
    invoiceLineDescriptionPlaceholder: $fn.value.pgettext(
      'InvoiceLines',
      'Description'
    ),
  });
  const invoiceLineHelper = ref(null);
  const ux = ref([]);
  const b_5962862420439144_rowHeader = computed(() => false);
  const b_5962862420439144_paging = computed(() => false);
  const b_5962862420439144_sortable = computed(() => false);
  const b_5962862420439144_rowTemplate = computed(() => ({
    description: null,
    discount: props.defaultDiscount || 0,
    notes: null,
  }));
  const b_5962862420439144_messages = computed(() => ({
    msgCreateRow: $fn.value.pgettext('InvoiceLines', 'Add line'),
    msgCreateProduct: $fn.value.pgettext('InvoiceLines', 'Add new product...'),
    msgSearchProduct: $fn.value.pgettext(
      'InvoiceLines',
      'Search for product/service...'
    ),
    msgSearchCostCenter: $fn.value.pgettext('InvoiceLines', 'search...'),
  }));
  const b_5962862420439144_columns = computed(() => [
    {
      name: 'product',
      header: $fn.value.gettext('Product'),
      width: '250px',
      required: true,
      textAlign: 'left',
      editor: {
        name: 'template',
        value:
          '<ItpSelect2\n\
  class="w-100"\n\
  v-model="currentValue"\n\
  bindObject\n\
  :name="col.field"\n\
  valueField="id"\n\
  textField="name"\n\
  required\n\
  :placeholder="messages.msgSearchProduct"\n\
  :options="globs.searchDataSource(\'suggest_product\', {invoice_line_products: \'include\'})"\n\
  :autofocus=autofocus\n\
  :form="formId"\n\
  :inputId="col.field"\n\
  :size="col.size"\n\
  :state="validationState"\n\
  :disabled="isDisabled"\n\
  :tabindex="col.tabindex"\n\
  taggable\n\
  @optionSelected="col.emit(\'product:selected\', {col, row, ...$event})"\n\
  @change="onChange($event)"\n\
>\n\
  <template #search="{attributes, events}">\n\
    <input\n\
      class="vs__search"\n\
      v-bind="attributes"\n\
      v-on="events"\n\
    />\n\
  </template>\n\
\n\
  <template #option="option">\n\
    <UcSuggestProductOption :option="option" v-if="option.object==\'product\'">\n\
    </UcSuggestProductOption>\n\
    <span v-else>{{ option.name }}</span>\n\
  </template>\n\
\n\
  <template #selected-option="option">\n\
    <UcSuggestProductOption :option="option" v-if="option.object==\'product\'">\n\
    </UcSuggestProductOption>\n\
    <span v-else>{{ option.name }}</span>\n\
  </template>\n\
\n\
  <template #list-footer>\n\
    <hr />\n\
    <ItpButton\n\
      class="ms-2"\n\
      size=sm\n\
      variant=light\n\
      @click="col.emitCellEvent(\'createProduct\', row)">\n\
      {{ messages.msgCreateProduct }}\n\
    </ItpButton>\n\
  </template>\n\
\n\
  <template #footer>\n\
    <BFormInput\n\
      class="mt-1"\n\
      type=text\n\
      v-model=row.description\n\
      id="description"\n\
      name="description"\n\
      autocomplete="off"\n\
      :form="formId"\n\
      :size=col.size\n\
      :disabled="isDisabled"\n\
      :placeholder="globs.messages.invoiceLineDescriptionPlaceholder"\n\
      @change="onFieldChange(\'description\', row.description)"\n\
    ></BFormInput>\n\
  </template>\n\
</ItpSelect2>\n\
',
      },
      formatter: {
        name: 'template',
        value:
          '<div class="text-truncate" :title="row.description">\n\
  <BLink v-if="row.product && row.product.id" class="fw-bold" :to="{\n\
    name: \'app.product\',\n\
    params: { id: row.product.id }\n\
  }">{{ row.description }}</BLink>\n\
  <span v-if="!row.product">\n\
    {{ row.description }}\n\
  </span>\n\
</div>\n\
',
      },
    },
    {
      name: 'advance',
      header: $fn.value.pgettext('InvoiceLine|ColHeader', 'Adv.'),
      headerTooltip: $fn.value.pgettext(
        'invoiceLine|ColHeader|Tooltip',
        'Advance'
      ),
      headerTextAlign: 'center',
      width: '25px',
      type: 'checkbox',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'intermediated_service',
      header: $fn.value.pgettext('AbbrIntermediatedService', 'IMS'),
      headerTooltip: $fn.value.gettext('Intermediated Service'),
      headerTextAlign: 'center',
      width: '25px',
      type: 'checkbox',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'quantity',
      type: 'number',
      maxAllowedDecimals: isIncomingInvoice.value
        ? null
        : props.currency.quantity_digits,
      default: 0,
      header: $fn.value.pgettext('Quantity Abbr', 'Qty'),
      headerTooltip: $fn.value.gettext('Quantity'),
      headerTextAlign: 'right',
      width: '60px',
      textAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'unit',
      header: $fn.value.pgettext('Unit Abbr', 'Unit'),
      headerTooltip: $fn.value.gettext('Unit'),
      width: '70px',
      textAlign: 'center',
      headerTextAlign: 'center',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'price',
      type: 'number',
      maxAllowedDecimals: isIncomingInvoice.value
        ? null
        : props.currency.price_digits,
      default: 0,
      header: $fn.value.gettext('Price'),
      width: '70px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
      footerTotals: (rows) => _.meanBy(rows, (p) => p.quantity * p.price),
    },
    {
      name: 'discount',
      type: 'number',
      maxAllowedDecimals: isIncomingInvoice.value
        ? null
        : props.currency.digits,
      default: props.defaultDiscount || 0,
      header: $fn.value.pgettext('AbbrDiscount', 'Disc'),
      headerTooltip: $fn.value.gettext('Discount'),
      noFooterTotals: true,
      width: '50px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'net_amount',
      type: 'number',
      maxAllowedDecimals: props.currency.digits,
      default: 0,
      header: $fn.value.gettext('Net'),
      width: '95px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'amount',
      type: 'number',
      maxAllowedDecimals: props.currency.digits,
      default: 0,
      header: $fn.value.gettext('Amount'),
      width: '95px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'tax_code',
      header: $fn.value.gettext('VAT'),
      headerTooltip: $fn.value.gettext('VAT Code'),
      width: '80px',
      type: 'select',
      options: taxCodes.value,
      valueField: 'id',
      textField: 'id',
      textAlign: 'right',
      headerTextAlign: 'right',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'deductible_vat_amount',
      type: 'number',
      disabled: isOutgoingInvoice.value,
      header: $fn.value.gettext('D-VAT'),
      headerTooltip: $fn.value.gettext('Deductible VAT amount'),
      maxAllowedDecimals: props.currency.digits,
      default: 0,
      width: '95px',
      textAlign: 'right',
      headerTextAlign: 'right',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'expense_type',
      type: 'select',
      header: $fn.value.pgettext('InvoiceLine|ColHeader', 'Expense Type'),
      width: '100px',
      options: expenseTypes.value,
      valueField: 'name',
      textField: 'name',
    },
    {
      name: 'rental_or_property',
      header: $fn.value.gettext('Cost Center'),
      textAlign: 'left',
      width: '100px',
      editor: {
        name: 'template',
        value:
          '<ItpSelect2\n\
  class="w-100"\n\
  v-model="currentValue"\n\
  bindObject\n\
  :name="col.field"\n\
  valueField="id"\n\
  textField="name"\n\
  required\n\
  :placeholder="messages.msgSearchCostCenter"\n\
  :options="globs.searchDataSource(\'suggest_cost_center\')"\n\
  :initialOptions="[row.rental || row.property]"\n\
  :form="formId"\n\
  :inputId="col.field"\n\
  :size="col.size"\n\
  :state="validationState"\n\
  :disabled="isDisabled"\n\
  :tabindex="col.tabindex"\n\
  @optionSelected="col.emit(\'rental_or_property:selected\', {col, row, ...$event})"\n\
  @change="onChange($event)"\n\
>\n\
  <template #search="{attributes, events}">\n\
    <input\n\
      class="vs__search"\n\
      v-bind="attributes"\n\
      v-on="events"\n\
    />\n\
  </template>\n\
\n\
  <template #option="option">\n\
    <UcSuggestRentalOption :option="option" v-if="option.object==\'rental\'">\n\
    </UcSuggestRentalOption>\n\
    <UcSuggestPropertyOption :option="option" v-if="option.object==\'property\' || option.object == \'property_unit\'">\n\
    </UcSuggestPropertyOption>\n\
  </template>\n\
\n\
  <template #selected-option="option">\n\
    <UcSuggestRentalOption :option="option" v-if="option.object==\'rental\'">\n\
    </UcSuggestRentalOption>\n\
    <UcSuggestPropertyOption :option="option" v-if="option.object==\'property\' || option.object == \'property_unit\'">\n\
    </UcSuggestPropertyOption>\n\
  </template>\n\
</ItpSelect2>\n\
',
      },
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <UcSuggestRentalOption :option="row.rental" v-if="row.rental">\n\
  </UcSuggestRentalOption>\n\
  <UcSuggestPropertyOption :option="row.property" v-if="row.property" display-code>\n\
  </UcSuggestPropertyOption>\n\
</div>\n\
',
      },
    },
  ]);
  const b_1270943406604881_dataSource = computed(() => ({
    name: 'suggest_invoice',
    parameters: {
      invoice_kind: props.invoice.invoice_kind,
      client_id: props.invoice.client.id,
      company_id: props.invoice.company.id,
      exclude_invoice: props.invoice.id,
    },
  }));
  const b_1803307708030275_paging = computed(() => false);
  const b_1803307708030275_sortable = computed(() => false);
  const b_1803307708030275_rowHeader = computed(() => false);
  const b_1803307708030275_fillEmptyRowSpace = computed(() => false);
  const b_1803307708030275_messages = computed(() => ({
    msgCreateRow: $fn.value.pgettext('invoiceLines', 'New meter row'),
  }));
  const b_1803307708030275_columns = computed(() => [
    {
      name: 'meter',
      header: $fn.value.gettext('Meter'),
      width: '160px',
      required: true,
      type: 'select2',
      options: $fn.value.searchDataSource('suggest_meter'),
      bindObject: true,
      valueField: 'id',
      textField: 'serial_number',
      formatter: {
        name: 'template',
        value:
          '<UcMeterMiniCard :item="row.meter" link-style-underline no-strong></UcMeterMiniCard>\n\
',
      },
    },
    {
      name: 'from',
      header: $fn.value.gettext('Previous reading'),
      width: '160px',
      formatter: {
        name: 'template',
        value:
          '<div>{{date(row.from_date) }} {{number(row.from_amount) }} {{ row.from_unit }}</div>\n\
',
      },
      editor: {
        name: 'template',
        value:
          '<div class="input-group">\n\
  <ItpFormDatetime\n\
    v-model="row.from_date"\n\
    name="from_date"\n\
    placeholder="date"\n\
    size="sm"\n\
    style="min-width:100px;"\n\
    required\n\
  ></ItpFormDatetime>\n\
\n\
  <ItpFormInput\n\
    type="number"\n\
    name="from_amount"\n\
    v-model="row.from_amount"\n\
    placeholder="reading"\n\
    size="sm"\n\
    style="min-width:60px;"\n\
    required\n\
  ></ItpFormInput>\n\
\n\
  <ItpFormSelect\n\
    v-model="row.from_unit"\n\
    name="from_unit"\n\
    placeholder="unit"\n\
    size="sm"\n\
    :options="globs.units"\n\
    style="min-width:60px;"\n\
    valueField="symbol"\n\
    textField="symbol"\n\
    required\n\
  ></ItpFormSelect>\n\
</div>\n\
',
      },
    },
    {
      name: 'to',
      header: $fn.value.gettext('Current reading'),
      width: '240px',
      formatter: {
        name: 'template',
        value:
          '<div>{{date(row.to_date) }} {{number(row.to_amount) }} {{ row.to_unit }}</div>\n\
',
      },
      editor: {
        name: 'template',
        value:
          '<div class="input-group">\n\
  <ItpFormDatetime\n\
    v-model="row.to_date"\n\
    name="to_date"\n\
    placeholder="date"\n\
    size="sm"\n\
    style="min-width:100px;"\n\
    required\n\
  ></ItpFormDatetime>\n\
\n\
  <ItpFormInput\n\
    type="number"\n\
    name="to_amount"\n\
    v-model="row.to_amount"\n\
    placeholder="reading"\n\
    size="sm"\n\
    style="min-width:60px;"\n\
    required\n\
  ></ItpFormInput>\n\
\n\
  <ItpFormSelect\n\
    v-model="row.to_unit"\n\
    name="to_unit"\n\
    placeholder="unit"\n\
    size="sm"\n\
    :options="globs.units"\n\
    style="min-width:60px;"\n\
    valueField="symbol"\n\
    textField="symbol"\n\
    required\n\
  ></ItpFormSelect>\n\
</div>\n\
',
      },
    },
    {
      name: 'consumption',
      header: $fn.value.gettext('Consumption'),
      readonly: true,
      formatter: {
        name: 'template',
        value:
          '<div>{{number(row.consumption_amount) }} {{ row.consumption_unit }}</div>\n\
',
      },
    },
    {
      name: 'billing_multiplier',
      header: $fn.value.gettext('Multiplier'),
      default: 1,
      width: '70px',
      type: 'number',
      required: true,
    },
  ]);
  const b_7700024326226090_paging = computed(() => false);
  const b_7700024326226090_sortable = computed(() => false);
  const b_7700024326226090_rowHeader = computed(() => false);
  const b_7700024326226090_fillEmptyRowSpace = computed(() => false);
  const b_7700024326226090_messages = computed(() => ({
    msgCreateRow: $fn.value.pgettext('invoiceLines', 'New accounting row'),
  }));
  const b_7700024326226090_columns = computed(() => [
    {
      name: 'expense_type',
      type: 'select',
      header: $fn.value.gettext('Expense Type'),
      width: '100px',
      options: expenseTypes.value,
      valueField: 'name',
      textField: 'name',
    },
    {
      name: 'rental',
      type: 'select2',
      header: $fn.value.gettext('Rental'),
      width: '100px',
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_rental'),
      useOptionTemplateForSelectedOption: true,
      optionTemplate:
        '<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>',
    },
    {
      name: 'property',
      type: 'select2',
      header: $fn.value.gettext('Property'),
      width: '100px',
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_property'),
      useOptionTemplateForSelectedOption: true,
      optionTemplate:
        '<UcSuggestPropertyOption :option="option"></UcSuggestPropertyOption>',
    },
    {
      name: 'contractor',
      type: 'select2',
      header: $fn.value.gettext('Contractor'),
      width: '100px',
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_company', {
        role: 'partner',
        'is-contractor': true,
      }),
    },
    {
      name: 'quantity',
      type: 'number',
      header: $fn.value.gettext('Quantity'),
      width: '100px',
    },
  ]);

  const $data = computed(() => ({
    expenseTypes: expenseTypes.value,
    taxCodes: taxCodes.value,
    currencies: currencies.value,
    units: units.value,
    displayAddInvoiceLine: displayAddInvoiceLine.value,
    messages: messages.value,
    invoiceLineHelper: invoiceLineHelper.value,
    ux: ux.value,
    b_5962862420439144_rowHeader: b_5962862420439144_rowHeader.value,
    b_5962862420439144_paging: b_5962862420439144_paging.value,
    b_5962862420439144_sortable: b_5962862420439144_sortable.value,
    b_5962862420439144_rowTemplate: b_5962862420439144_rowTemplate.value,
    b_5962862420439144_messages: b_5962862420439144_messages.value,
    b_5962862420439144_columns: b_5962862420439144_columns.value,
    b_1270943406604881_dataSource: b_1270943406604881_dataSource.value,
    b_1803307708030275_paging: b_1803307708030275_paging.value,
    b_1803307708030275_sortable: b_1803307708030275_sortable.value,
    b_1803307708030275_rowHeader: b_1803307708030275_rowHeader.value,
    b_1803307708030275_fillEmptyRowSpace:
      b_1803307708030275_fillEmptyRowSpace.value,
    b_1803307708030275_messages: b_1803307708030275_messages.value,
    b_1803307708030275_columns: b_1803307708030275_columns.value,
    b_7700024326226090_paging: b_7700024326226090_paging.value,
    b_7700024326226090_sortable: b_7700024326226090_sortable.value,
    b_7700024326226090_rowHeader: b_7700024326226090_rowHeader.value,
    b_7700024326226090_fillEmptyRowSpace:
      b_7700024326226090_fillEmptyRowSpace.value,
    b_7700024326226090_messages: b_7700024326226090_messages.value,
    b_7700024326226090_columns: b_7700024326226090_columns.value,
  }));

  const $$load_expenseTypes = async () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'expense-type' },
    });
  };
  const $$load_taxCodes = async () => {
    return $app.value.repo.get('list_tax_code');
  };
  const $$load_currencies = async () => {
    return $app.value.repo.get('list_currency');
  };
  const $$load_units = async () => {
    return $app.value.repo.get('list_unit');
  };
  const $$load_invoiceLineHelper = async () => {
    return new $app.value.helpers.InvoiceLineHelper(
      props.invoice,
      $data.value.currencies,
      $data.value.taxCodes
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'expenseTypes' },
      { name: 'taxCodes' },
      { name: 'currencies' },
      { name: 'units' },
      { name: 'displayAddInvoiceLine' },
      { name: 'messages' },
      { name: 'invoiceLineHelper', dependsOn: ['currencies', 'taxCodes'] },
      { name: 'ux' },
      { name: 'b_5962862420439144_rowHeader', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_paging', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_sortable', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_rowTemplate', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_messages', nodeId: '5962862420439144' },
      {
        name: 'b_5962862420439144_columns',
        nodeId: '5962862420439144',
        dependsOn: ['expenseTypes', 'taxCodes'],
      },
      { name: 'b_1270943406604881_dataSource', nodeId: '1270943406604881' },
      { name: 'b_1803307708030275_paging', nodeId: '1803307708030275' },
      { name: 'b_1803307708030275_sortable', nodeId: '1803307708030275' },
      { name: 'b_1803307708030275_rowHeader', nodeId: '1803307708030275' },
      {
        name: 'b_1803307708030275_fillEmptyRowSpace',
        nodeId: '1803307708030275',
      },
      { name: 'b_1803307708030275_messages', nodeId: '1803307708030275' },
      { name: 'b_1803307708030275_columns', nodeId: '1803307708030275' },
      { name: 'b_7700024326226090_paging', nodeId: '7700024326226090' },
      { name: 'b_7700024326226090_sortable', nodeId: '7700024326226090' },
      { name: 'b_7700024326226090_rowHeader', nodeId: '7700024326226090' },
      {
        name: 'b_7700024326226090_fillEmptyRowSpace',
        nodeId: '7700024326226090',
      },
      { name: 'b_7700024326226090_messages', nodeId: '7700024326226090' },
      {
        name: 'b_7700024326226090_columns',
        nodeId: '7700024326226090',
        dependsOn: ['expenseTypes'],
      },
    ],
    loaders: {
      expenseTypes: $$load_expenseTypes,
      taxCodes: $$load_taxCodes,
      currencies: $$load_currencies,
      units: $$load_units,
      invoiceLineHelper: $$load_invoiceLineHelper,
    },
    ux: {},
    dataRefs: {
      expenseTypes,
      taxCodes,
      currencies,
      units,
      displayAddInvoiceLine,
      messages,
      invoiceLineHelper,
      ux,
      b_5962862420439144_rowHeader,
      b_5962862420439144_paging,
      b_5962862420439144_sortable,
      b_5962862420439144_rowTemplate,
      b_5962862420439144_messages,
      b_5962862420439144_columns,
      b_1270943406604881_dataSource,
      b_1803307708030275_paging,
      b_1803307708030275_sortable,
      b_1803307708030275_rowHeader,
      b_1803307708030275_fillEmptyRowSpace,
      b_1803307708030275_messages,
      b_1803307708030275_columns,
      b_7700024326226090_paging,
      b_7700024326226090_sortable,
      b_7700024326226090_rowHeader,
      b_7700024326226090_fillEmptyRowSpace,
      b_7700024326226090_messages,
      b_7700024326226090_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const invoiceLines = computed(() => {
    return props.invoice.lines.data;
  });
  const sortedInvoiceLines = computed(() => {
    return _.orderBy(props.invoice.lines.data, [
      (p) => (p.order_index == null ? Infinity : p.order_index),
    ]);
  });
  const invoiceLineGroups = computed(() => {
    return _.orderBy(props.invoice.line_groups.data, [
      (p) => (p.order_index == null ? Infinity : p.order_index),
    ]);
  });
  const isIncomingInvoice = computed(() => {
    return props.invoice.invoice_kind == 'incoming';
  });
  const isOutgoingInvoice = computed(() => {
    return props.invoice.invoice_kind == 'outgoing';
  });
  const hasSelectedInvoiceLines = computed(() => {
    return (
      props.formContext.selectedInvoiceLines &&
      props.formContext.selectedInvoiceLines.length > 0
    );
  });
  const invoiceRentalNumber = computed(() => {
    return _.chain(props.invoice.references.data)
      .find({ object: 'rental' })
      .get('name')
      .value();
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.currency,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('currency');
    }
  );
  watch(
    () => props.defaultDiscount,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('defaultDiscount');
    }
  );
  watch(
    () => props.defaultTaxCode,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('defaultTaxCode');
    }
  );
  watch(
    () => props.formContext,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formContext');
    }
  );

  const act_1889980785424776_showModal_3als2a8ec_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'UploadInvoiceLines',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_4892266742011185_setData_lu7amf5tu_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'formContext.selectedInvoiceLines',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4892266742011185_emit_afzwyxc8f_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4892266742011185_request_62dlu1x3z_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_many_invoice_line',
      data: {
        invoice_id: props.invoice.id,
        items: _.map(props.formContext.selectedInvoiceLines, (p) => ({
          id: p.data.id,
          etag: p.data.etag,
        })),
      },
    };
  };
  const act_5962862420439144_emit_tiv6g181y_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5962862420439144_script_77bvz5ky4_value_callable = async (
    $event
  ) => {
    return props.invoice.id
      ? $event.data.waitFor(
          $fn.value
            .crud('invoice_line')
            .upsert({ ...$event.data.row, invoice_id: props.invoice.id })
        )
      : invoiceLines.value.splice(
          invoiceLines.value.length - 1,
          0,
          $event.data.row
        );
  };
  const act_5962862420439144_script_77bvz5ky4_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_77bvz5ky4_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_4e0l2ybe3_value_callable = async (
    $event
  ) => {
    return onProductSelected(
      $event.parent.data.row,
      $event.data.modalResult.value
    );
  };
  const act_5962862420439144_script_4e0l2ybe3_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_4e0l2ybe3_value_callable($event);
      },
    };
  };
  const act_5962862420439144_showModal_6286d0v5g_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProduct',
      props: {
        preventDefaultOnClose: true,
      },
    };
  };
  const act_5962862420439144_script_8916ewzqr_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line').delete($event.data.row)
    );
  };
  const act_5962862420439144_script_8916ewzqr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_8916ewzqr_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_lo8jtf8sz_value_callable = async (
    $event
  ) => {
    return onProductSelected($event.data.row, $event.data.option);
  };
  const act_5962862420439144_script_lo8jtf8sz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_lo8jtf8sz_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_kth3oo346_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_kth3oo346_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_kth3oo346_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_oj3l2hos1_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_oj3l2hos1_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_oj3l2hos1_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_jbm07rwyd_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_jbm07rwyd_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_jbm07rwyd_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_3lm605vs5_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_3lm605vs5_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_3lm605vs5_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_7qorwwqmh_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_7qorwwqmh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_7qorwwqmh_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_bdez5rebc_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_bdez5rebc_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_bdez5rebc_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_7ct6vy3cv_value_callable = async (
    $event
  ) => {
    return onRentalOrPropertySelected($event.data.row, $event.data.option);
  };
  const act_5962862420439144_script_7ct6vy3cv_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_7ct6vy3cv_value_callable($event);
      },
    };
  };
  const act_5962862420439144_setData_u0m0moqa1_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'formContext.selectedInvoiceLines',
      value: $event.data.selection,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_emit_x6l0rr1a1_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_309515315410323_emit_cv2lf7f16_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_309515315410323_close_29j45uzyl_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_309515315410323_showModal_w4gsuu8ng_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceLineGroup',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_880971572885910_script_mw3523olh_value_callable = async (
    $event
  ) => {
    return $fn.value
      .crud('invoice_line')
      .upsert({
        id: $event.scope.row.data.id,
        etag: $event.scope.row.data.etag,
        meter_accounting: {
          invoice_id: props.invoice.id,
          invoice_line_id: $event.scope.row.data.id,
          ...$event.data.modalResult.value,
        },
      })
      .then(() => emit('reload'));
  };
  const act_880971572885910_script_mw3523olh_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_880971572885910_script_mw3523olh_value_callable($event);
      },
    };
  };
  const inst_evh_880971572885910_close_l5un9b5fd_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_880971572885910_showModal_drrqvo3mb_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceLineMeterReadingSelector',
      props: {
        rental: invoiceRentalNumber.value,
        multiple: false,
        serviceId: $event.scope.row.data.product
          ? $event.scope.row.data.product.id
          : null,
      },
    };
  };
  const act_1803307708030275_script_l6hisokk6_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line').upsert({
        id: $event.scope.row.data.id,
        etag: $event.scope.row.data.etag,
        meter_accounting: $event.data.row,
      })
    );
  };
  const act_1803307708030275_script_l6hisokk6_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1803307708030275_script_l6hisokk6_value_callable($event);
      },
    };
  };
  const act_1803307708030275_emit_asactbnnt_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_1803307708030275_script_nou5gtare_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line_meter_accounting').delete($event.data.row)
    );
  };
  const act_1803307708030275_script_nou5gtare_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1803307708030275_script_nou5gtare_value_callable($event);
      },
    };
  };
  const act_1803307708030275_emit_pw33d9pr5_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7700024326226090_script_h45me0puh_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('invoice_line_accounting')
        .upsert($event.data.row)
        .then((p) => (emit('reload'), p))
    );
  };
  const act_7700024326226090_script_h45me0puh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7700024326226090_script_h45me0puh_value_callable($event);
      },
    };
  };
  const act_7700024326226090_script_3kny3hujp_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line_accounting').delete($event.data.row)
    );
  };
  const act_7700024326226090_script_3kny3hujp_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7700024326226090_script_3kny3hujp_value_callable($event);
      },
    };
  };
  const act_7700024326226090_emit_jk8fs6qbu_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7700024326226090_emit_i1zjqsz87_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_1889980785424776_showModal_3als2a8ec =
    new actionTypes.ShowModalAction({
      actionArgs: act_1889980785424776_showModal_3als2a8ec_getActionArgs,
    });
  const inst_evh_1889980785424776_clickPreventStop_6i14tffja =
    new actionTypes.EventHandlerImpl({
      action: act_1889980785424776_showModal_3als2a8ec,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_1889980785424776_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1889980785424776_clickPreventStop_6i14tffja],
      event: 'click.prevent.stop',
    });
  const act_4892266742011185_setData_lu7amf5tu = new actionTypes.SetDataAction({
    actionArgs: act_4892266742011185_setData_lu7amf5tu_getActionArgs,
  });
  const inst_evh_4892266742011185_success_q6pvuez5y =
    new actionTypes.EventHandlerImpl({
      action: act_4892266742011185_setData_lu7amf5tu,
      event: 'success',
      displayName: 'setData',
    });
  const act_4892266742011185_emit_afzwyxc8f = new actionTypes.EmitAction({
    actionArgs: act_4892266742011185_emit_afzwyxc8f_getActionArgs,
  });
  const inst_evh_4892266742011185_success_02msh44sl =
    new actionTypes.EventHandlerImpl({
      action: act_4892266742011185_emit_afzwyxc8f,
      event: 'success',
      displayName: 'emit',
    });
  const act_4892266742011185_request_62dlu1x3z = new actionTypes.RequestAction({
    actionArgs: act_4892266742011185_request_62dlu1x3z_getActionArgs,
    displayName: 'deleteSelectedLines',
    events: [
      inst_evh_4892266742011185_success_q6pvuez5y,
      inst_evh_4892266742011185_success_02msh44sl,
    ],
  });
  const inst_evh_4892266742011185_clickPreventStop_o4uaxlgul =
    new actionTypes.EventHandlerImpl({
      action: act_4892266742011185_request_62dlu1x3z,
      event: 'click.prevent.stop',
      displayName: 'deleteSelectedLines',
    });
  const inst_evh_4892266742011185_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4892266742011185_clickPreventStop_o4uaxlgul],
      event: 'click.prevent.stop',
    });
  const act_5962862420439144_emit_tiv6g181y = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_tiv6g181y_getActionArgs,
  });
  const inst_evh_5962862420439144_saveCompleted_iqknem20x =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_tiv6g181y,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_saveCompleted_iqknem20x],
      event: 'save-completed',
    });
  const act_5962862420439144_script_77bvz5ky4 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_77bvz5ky4_getActionArgs,
  });
  const inst_evh_5962862420439144_save_e88gzkblw =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_77bvz5ky4,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_save_e88gzkblw],
    event: 'save',
  });
  const act_5962862420439144_script_4e0l2ybe3 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_4e0l2ybe3_getActionArgs,
  });
  const inst_evh_5962862420439144_close_p4od9irku =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_4e0l2ybe3,
      event: 'close',
      displayName: 'script',
    });
  const act_5962862420439144_showModal_6286d0v5g =
    new actionTypes.ShowModalAction({
      actionArgs: act_5962862420439144_showModal_6286d0v5g_getActionArgs,
      events: [inst_evh_5962862420439144_close_p4od9irku],
    });
  const inst_evh_5962862420439144_createProduct_rqrgmv4j1 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_showModal_6286d0v5g,
      event: 'createProduct',
      displayName: 'showModal',
    });
  const inst_evh_5962862420439144_createProduct =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_createProduct_rqrgmv4j1],
      event: 'createProduct',
    });
  const act_5962862420439144_script_8916ewzqr = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_8916ewzqr_getActionArgs,
  });
  const inst_evh_5962862420439144_delete_28m25shl1 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_8916ewzqr,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_delete_28m25shl1],
    event: 'delete',
  });
  const act_5962862420439144_script_lo8jtf8sz = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_lo8jtf8sz_getActionArgs,
  });
  const inst_evh_5962862420439144_productSelected_8iqk6aty3 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_lo8jtf8sz,
      event: 'product:selected',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_productSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_productSelected_8iqk6aty3],
      event: 'product:selected',
    });
  const act_5962862420439144_script_kth3oo346 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_kth3oo346_getActionArgs,
  });
  const inst_evh_5962862420439144_quantityChange_rup1y07dz =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_kth3oo346,
      event: 'quantity:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_quantityChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_quantityChange_rup1y07dz],
      event: 'quantity:change',
    });
  const act_5962862420439144_script_oj3l2hos1 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_oj3l2hos1_getActionArgs,
  });
  const inst_evh_5962862420439144_priceChange_67fy40ld7 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_oj3l2hos1,
      event: 'price:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_priceChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_priceChange_67fy40ld7],
      event: 'price:change',
    });
  const act_5962862420439144_script_jbm07rwyd = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_jbm07rwyd_getActionArgs,
  });
  const inst_evh_5962862420439144_discountChange_8gy2hwtkl =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_jbm07rwyd,
      event: 'discount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_discountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_discountChange_8gy2hwtkl],
      event: 'discount:change',
    });
  const act_5962862420439144_script_3lm605vs5 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_3lm605vs5_getActionArgs,
  });
  const inst_evh_5962862420439144_netAmountChange_5bmwmsi0b =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_3lm605vs5,
      event: 'net_amount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_netAmountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_netAmountChange_5bmwmsi0b],
      event: 'net_amount:change',
    });
  const act_5962862420439144_script_7qorwwqmh = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_7qorwwqmh_getActionArgs,
  });
  const inst_evh_5962862420439144_amountChange_krafgzp1m =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_7qorwwqmh,
      event: 'amount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_amountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_amountChange_krafgzp1m],
      event: 'amount:change',
    });
  const act_5962862420439144_script_bdez5rebc = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_bdez5rebc_getActionArgs,
  });
  const inst_evh_5962862420439144_taxCodeChange_vqrdgjb0i =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_bdez5rebc,
      event: 'tax_code:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_taxCodeChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_taxCodeChange_vqrdgjb0i],
      event: 'tax_code:change',
    });
  const act_5962862420439144_script_7ct6vy3cv = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_7ct6vy3cv_getActionArgs,
  });
  const inst_evh_5962862420439144_rentalOrPropertySelected_e1vqtqv75 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_7ct6vy3cv,
      event: 'rental_or_property:selected',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_rentalOrPropertySelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_rentalOrPropertySelected_e1vqtqv75],
      event: 'rental_or_property:selected',
    });
  const act_5962862420439144_setData_u0m0moqa1 = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_u0m0moqa1_getActionArgs,
  });
  const inst_evh_5962862420439144_selectedRowsChanged_pwo5hyd11 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_u0m0moqa1,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_5962862420439144_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_selectedRowsChanged_pwo5hyd11],
      event: 'selectedRowsChanged',
    });
  const act_7789867021953041_emit_x6l0rr1a1 = new actionTypes.EmitAction({
    actionArgs: act_7789867021953041_emit_x6l0rr1a1_getActionArgs,
  });
  const inst_evh_7789867021953041_reload_6qo1yt9np =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_emit_x6l0rr1a1,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_7789867021953041_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7789867021953041_reload_6qo1yt9np],
    event: 'reload',
  });
  const act_309515315410323_emit_cv2lf7f16 = new actionTypes.EmitAction({
    actionArgs: act_309515315410323_emit_cv2lf7f16_getActionArgs,
  });
  const inst_evh_309515315410323_close_29j45uzyl =
    new actionTypes.EventHandlerImpl({
      action: act_309515315410323_emit_cv2lf7f16,
      event: 'close',
      when: inst_evh_309515315410323_close_29j45uzyl_getWhen,
      displayName: 'emit',
    });
  const act_309515315410323_showModal_w4gsuu8ng =
    new actionTypes.ShowModalAction({
      actionArgs: act_309515315410323_showModal_w4gsuu8ng_getActionArgs,
      events: [inst_evh_309515315410323_close_29j45uzyl],
    });
  const inst_evh_309515315410323_clickPreventStop_3qf79gilg =
    new actionTypes.EventHandlerImpl({
      action: act_309515315410323_showModal_w4gsuu8ng,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_309515315410323_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_309515315410323_clickPreventStop_3qf79gilg],
      event: 'click.prevent.stop',
    });
  const act_880971572885910_script_mw3523olh = new actionTypes.ScriptAction({
    actionArgs: act_880971572885910_script_mw3523olh_getActionArgs,
  });
  const inst_evh_880971572885910_close_l5un9b5fd =
    new actionTypes.EventHandlerImpl({
      action: act_880971572885910_script_mw3523olh,
      event: 'close',
      when: inst_evh_880971572885910_close_l5un9b5fd_getWhen,
      displayName: 'script',
    });
  const act_880971572885910_showModal_drrqvo3mb =
    new actionTypes.ShowModalAction({
      actionArgs: act_880971572885910_showModal_drrqvo3mb_getActionArgs,
      events: [inst_evh_880971572885910_close_l5un9b5fd],
    });
  const inst_evh_880971572885910_clickPreventStop_rvy57kikk =
    new actionTypes.EventHandlerImpl({
      action: act_880971572885910_showModal_drrqvo3mb,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_880971572885910_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_880971572885910_clickPreventStop_rvy57kikk],
      event: 'click.prevent.stop',
    });
  const act_1803307708030275_script_l6hisokk6 = new actionTypes.ScriptAction({
    actionArgs: act_1803307708030275_script_l6hisokk6_getActionArgs,
  });
  const inst_evh_1803307708030275_save_1e82v1p61 =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_script_l6hisokk6,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_1803307708030275_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1803307708030275_save_1e82v1p61],
    event: 'save',
  });
  const act_1803307708030275_emit_asactbnnt = new actionTypes.EmitAction({
    actionArgs: act_1803307708030275_emit_asactbnnt_getActionArgs,
  });
  const inst_evh_1803307708030275_saveCompleted_xfdlp8t0j =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_emit_asactbnnt,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_1803307708030275_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1803307708030275_saveCompleted_xfdlp8t0j],
      event: 'save-completed',
    });
  const act_1803307708030275_script_nou5gtare = new actionTypes.ScriptAction({
    actionArgs: act_1803307708030275_script_nou5gtare_getActionArgs,
  });
  const inst_evh_1803307708030275_delete_n75rkyaqo =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_script_nou5gtare,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_1803307708030275_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1803307708030275_delete_n75rkyaqo],
    event: 'delete',
  });
  const act_1803307708030275_emit_pw33d9pr5 = new actionTypes.EmitAction({
    actionArgs: act_1803307708030275_emit_pw33d9pr5_getActionArgs,
  });
  const inst_evh_1803307708030275_deleteCompleted_mhm0jpbbp =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_emit_pw33d9pr5,
      event: 'delete-completed',
      displayName: 'emit',
    });
  const inst_evh_1803307708030275_deleteCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1803307708030275_deleteCompleted_mhm0jpbbp],
      event: 'delete-completed',
    });
  const act_7700024326226090_script_h45me0puh = new actionTypes.ScriptAction({
    actionArgs: act_7700024326226090_script_h45me0puh_getActionArgs,
  });
  const inst_evh_7700024326226090_save_fmgi0nq4o =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_script_h45me0puh,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_7700024326226090_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7700024326226090_save_fmgi0nq4o],
    event: 'save',
  });
  const act_7700024326226090_script_3kny3hujp = new actionTypes.ScriptAction({
    actionArgs: act_7700024326226090_script_3kny3hujp_getActionArgs,
  });
  const inst_evh_7700024326226090_delete_htp0gqrum =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_script_3kny3hujp,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_7700024326226090_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7700024326226090_delete_htp0gqrum],
    event: 'delete',
  });
  const act_7700024326226090_emit_jk8fs6qbu = new actionTypes.EmitAction({
    actionArgs: act_7700024326226090_emit_jk8fs6qbu_getActionArgs,
  });
  const inst_evh_7700024326226090_saveCompleted_gy3m84ax1 =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_emit_jk8fs6qbu,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_7700024326226090_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7700024326226090_saveCompleted_gy3m84ax1],
      event: 'save-completed',
    });
  const act_7700024326226090_emit_i1zjqsz87 = new actionTypes.EmitAction({
    actionArgs: act_7700024326226090_emit_i1zjqsz87_getActionArgs,
  });
  const inst_evh_7700024326226090_deleteCompleted_85knhe3g1 =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_emit_i1zjqsz87,
      event: 'delete-completed',
      displayName: 'emit',
    });
  const inst_evh_7700024326226090_deleteCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7700024326226090_deleteCompleted_85knhe3g1],
      event: 'delete-completed',
    });

  const actions = {
    inst_evh_1889980785424776_clickPreventStop_6i14tffja,
    inst_evh_1889980785424776_clickPreventStop,
    inst_evh_4892266742011185_success_q6pvuez5y,
    inst_evh_4892266742011185_success_02msh44sl,
    inst_evh_4892266742011185_clickPreventStop_o4uaxlgul,
    deleteSelectedLines: inst_evh_4892266742011185_clickPreventStop_o4uaxlgul,
    inst_evh_4892266742011185_clickPreventStop,
    inst_evh_5962862420439144_saveCompleted_iqknem20x,
    inst_evh_5962862420439144_saveCompleted,
    inst_evh_5962862420439144_save_e88gzkblw,
    inst_evh_5962862420439144_save,
    inst_evh_5962862420439144_close_p4od9irku,
    inst_evh_5962862420439144_createProduct_rqrgmv4j1,
    inst_evh_5962862420439144_createProduct,
    inst_evh_5962862420439144_delete_28m25shl1,
    inst_evh_5962862420439144_delete,
    inst_evh_5962862420439144_productSelected_8iqk6aty3,
    inst_evh_5962862420439144_productSelected,
    inst_evh_5962862420439144_quantityChange_rup1y07dz,
    inst_evh_5962862420439144_quantityChange,
    inst_evh_5962862420439144_priceChange_67fy40ld7,
    inst_evh_5962862420439144_priceChange,
    inst_evh_5962862420439144_discountChange_8gy2hwtkl,
    inst_evh_5962862420439144_discountChange,
    inst_evh_5962862420439144_netAmountChange_5bmwmsi0b,
    inst_evh_5962862420439144_netAmountChange,
    inst_evh_5962862420439144_amountChange_krafgzp1m,
    inst_evh_5962862420439144_amountChange,
    inst_evh_5962862420439144_taxCodeChange_vqrdgjb0i,
    inst_evh_5962862420439144_taxCodeChange,
    inst_evh_5962862420439144_rentalOrPropertySelected_e1vqtqv75,
    inst_evh_5962862420439144_rentalOrPropertySelected,
    inst_evh_5962862420439144_selectedRowsChanged_pwo5hyd11,
    inst_evh_5962862420439144_selectedRowsChanged,
    inst_evh_7789867021953041_reload_6qo1yt9np,
    inst_evh_7789867021953041_reload,
    inst_evh_309515315410323_close_29j45uzyl,
    inst_evh_309515315410323_clickPreventStop_3qf79gilg,
    inst_evh_309515315410323_clickPreventStop,
    inst_evh_880971572885910_close_l5un9b5fd,
    inst_evh_880971572885910_clickPreventStop_rvy57kikk,
    inst_evh_880971572885910_clickPreventStop,
    inst_evh_1803307708030275_save_1e82v1p61,
    inst_evh_1803307708030275_save,
    inst_evh_1803307708030275_saveCompleted_xfdlp8t0j,
    inst_evh_1803307708030275_saveCompleted,
    inst_evh_1803307708030275_delete_n75rkyaqo,
    inst_evh_1803307708030275_delete,
    inst_evh_1803307708030275_deleteCompleted_mhm0jpbbp,
    inst_evh_1803307708030275_deleteCompleted,
    inst_evh_7700024326226090_save_fmgi0nq4o,
    inst_evh_7700024326226090_save,
    inst_evh_7700024326226090_delete_htp0gqrum,
    inst_evh_7700024326226090_delete,
    inst_evh_7700024326226090_saveCompleted_gy3m84ax1,
    inst_evh_7700024326226090_saveCompleted,
    inst_evh_7700024326226090_deleteCompleted_85knhe3g1,
    inst_evh_7700024326226090_deleteCompleted,
  };

  const evh_1889980785424776_clickPreventStop_6i14tffja = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1889980785424776_clickPreventStop_6i14tffja.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_success_q6pvuez5y = async (event, scope) => {
    return await actions.inst_evh_4892266742011185_success_q6pvuez5y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_success_02msh44sl = async (event, scope) => {
    return await actions.inst_evh_4892266742011185_success_02msh44sl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_clickPreventStop_o4uaxlgul = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4892266742011185_clickPreventStop_o4uaxlgul.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4892266742011185_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted_iqknem20x = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted_iqknem20x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save_e88gzkblw = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save_e88gzkblw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_close_p4od9irku = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_close_p4od9irku.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_createProduct_rqrgmv4j1 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_createProduct_rqrgmv4j1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_createProduct = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_createProduct.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete_28m25shl1 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete_28m25shl1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_productSelected_8iqk6aty3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_productSelected_8iqk6aty3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_productSelected = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_productSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_quantityChange_rup1y07dz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_quantityChange_rup1y07dz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_quantityChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_quantityChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_priceChange_67fy40ld7 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_priceChange_67fy40ld7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_priceChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_priceChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_discountChange_8gy2hwtkl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_discountChange_8gy2hwtkl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_discountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_discountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_netAmountChange_5bmwmsi0b = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_netAmountChange_5bmwmsi0b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_netAmountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_netAmountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_amountChange_krafgzp1m = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_amountChange_krafgzp1m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_amountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_amountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_taxCodeChange_vqrdgjb0i = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_taxCodeChange_vqrdgjb0i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_taxCodeChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_taxCodeChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_rentalOrPropertySelected_e1vqtqv75 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_rentalOrPropertySelected_e1vqtqv75.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_rentalOrPropertySelected = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_rentalOrPropertySelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_selectedRowsChanged_pwo5hyd11 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_selectedRowsChanged_pwo5hyd11.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_reload_6qo1yt9np = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_reload_6qo1yt9np.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_reload = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_close_29j45uzyl = async (event, scope) => {
    return await actions.inst_evh_309515315410323_close_29j45uzyl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_clickPreventStop_3qf79gilg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_309515315410323_clickPreventStop_3qf79gilg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_309515315410323_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_880971572885910_close_l5un9b5fd = async (event, scope) => {
    return await actions.inst_evh_880971572885910_close_l5un9b5fd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_880971572885910_clickPreventStop_rvy57kikk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_880971572885910_clickPreventStop_rvy57kikk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_880971572885910_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_880971572885910_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_save_1e82v1p61 = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_save_1e82v1p61.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_save = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_saveCompleted_xfdlp8t0j = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_saveCompleted_xfdlp8t0j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_delete_n75rkyaqo = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_delete_n75rkyaqo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_delete = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_deleteCompleted_mhm0jpbbp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1803307708030275_deleteCompleted_mhm0jpbbp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_deleteCompleted = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_deleteCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_save_fmgi0nq4o = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_save_fmgi0nq4o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_save = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_delete_htp0gqrum = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_delete_htp0gqrum.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_delete = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_saveCompleted_gy3m84ax1 = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_saveCompleted_gy3m84ax1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_deleteCompleted_85knhe3g1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7700024326226090_deleteCompleted_85knhe3g1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_deleteCompleted = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_deleteCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" alignItems="stretch" v-if="loaded">
    <ItpBox alignItems="center" class="pb-2">
      <strong>
        {{ pgettext('Invoice', 'Invoice Lines') }}
      </strong>
      <strong class="ms-1" v-if="invoiceLines.length">
        ({{ invoiceLines.length }})
      </strong>
      <ItpButton
        :icon="$config.uploadIcon"
        size="sm"
        :text="pgettext('Invoice|InvoiceLines', 'Upload...')"
        :tooltip="
          pgettext(
            'Invoice|InvoiceLines|Tooltip',
            'Upload invoice lines from file.'
          )
        "
        class="ms-3"
        v-if="!invoice.isReadOnly"
        @click.prevent.stop="evh_1889980785424776_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        :icon="$config.deleteIcon"
        size="sm"
        :text="pgettext('Invoice|InvoiceLines|Command', 'Delete')"
        :tooltip="
          pgettext('Invoice|InvoiceLines|Tooltip', 'Remove the selected lines.')
        "
        :disabled="
          !hasSelectedInvoiceLines || actions.deleteSelectedLines.isRunning
        "
        :spinning="actions.deleteSelectedLines.isRunning"
        class="ms-3"
        v-if="!invoice.isReadOnly"
        @click.prevent.stop="evh_4892266742011185_clickPreventStop($event, {})"
      >
      </ItpButton>
    </ItpBox>
    <ItpDataTable
      rowKey="id"
      tableId="invoiceLines"
      rowDetailsMode="alwaysVisible"
      :rowHeader="b_5962862420439144_rowHeader"
      :paging="b_5962862420439144_paging"
      :sortable="b_5962862420439144_sortable"
      :loading="actions.deleteSelectedLines.isRunning"
      :editable="!invoice.isReadOnly"
      :value="sortedInvoiceLines"
      :groups="invoiceLineGroups"
      groupKeyField="line_group_id"
      variant="details"
      row-selection
      columnHeaderStyle="cellheaderWhenEditing"
      cellClass="fw-bold"
      footer-totals
      :rowTemplate="b_5962862420439144_rowTemplate"
      :messages="b_5962862420439144_messages"
      :globs="{ searchDataSource, messages }"
      :columns="b_5962862420439144_columns"
      class="h-100"
      @save-completed="evh_5962862420439144_saveCompleted($event, {})"
      @save="evh_5962862420439144_save($event, {})"
      @createProduct="evh_5962862420439144_createProduct($event, {})"
      @delete="evh_5962862420439144_delete($event, {})"
      @product:selected="evh_5962862420439144_productSelected($event, {})"
      @quantity:change="evh_5962862420439144_quantityChange($event, {})"
      @price:change="evh_5962862420439144_priceChange($event, {})"
      @discount:change="evh_5962862420439144_discountChange($event, {})"
      @net_amount:change="evh_5962862420439144_netAmountChange($event, {})"
      @amount:change="evh_5962862420439144_amountChange($event, {})"
      @tax_code:change="evh_5962862420439144_taxCodeChange($event, {})"
      @rental_or_property:selected="
        evh_5962862420439144_rentalOrPropertySelected($event, {})
      "
      @selectedRowsChanged="
        evh_5962862420439144_selectedRowsChanged($event, {})
      "
    >
      <template v-slot:rowGroupHeader="{ group, rows }">
        <ItpBox>
          <UcInvoiceLineGroup
            :invoice="invoice"
            :group="group"
            @reload="evh_7789867021953041_reload($event, { group, rows })"
          >
          </UcInvoiceLineGroup>
        </ItpBox>
      </template>
      <template v-slot:afterLastRow="{ table }">
        <ItpBox>
          <ItpLink
            :icon="$config.addIcon"
            size="sm"
            :text="pgettext('InvoiceLineGroup', 'Add group...')"
            class="ms-3"
            @click.prevent.stop="
              evh_309515315410323_clickPreventStop($event, { table })
            "
          >
          </ItpLink>
        </ItpBox>
      </template>
      <template v-slot:rowDetails="{ row }">
        <div class="container-fluid px-lg-1">
          <ItpRow class="g-0">
            <ItpCol :w="2">
              <ItpText
                pre
                :text="row.data.notes"
                collapsible
                v-if="row.data.notes && !row.isEditing.value"
              >
              </ItpText>
              <ItpFormTextarea
                :rows="3"
                name="notes"
                :placeholder="gettext('Notes')"
                v-model="row.data.notes"
                :value="row.data.notes"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (row.data.notes =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :disabled="!row.isEditing.value"
                class="mb-2"
                v-if="row.isEditing.value"
              >
              </ItpFormTextarea>
              <ItpSelect2
                name="reference_invoice_id"
                :placeholder="pgettext('InvoiceLine', 'Reference invoice')"
                v-model="row.data.reference_invoice_id"
                :value="row.data.reference_invoice_id"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (row.data.reference_invoice_id =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :initialOptions="
                  row.data.reference_invoice ? [row.data.reference_invoice] : []
                "
                :dataSource="b_1270943406604881_dataSource"
                valueField="id"
                clearable
                optionTemplate="<UcSuggestInvoiceOption :option=option></UcSuggestInvoiceOption>"
                use-option-template-for-selected-option
                v-if="row.isEditing.value"
              >
              </ItpSelect2>
            </ItpCol>
            <ItpCol :w="10">
              <ItpBox
                v-if="
                  !row.isNew.value &&
                  (row.isEditing.value || row.data.meter_accounting) &&
                  invoiceRentalNumber
                "
              >
                <ItpLink
                  :icon="$config.addIcon"
                  size="sm"
                  :text="pgettext('InvoiceLines', 'Select readings...')"
                  @click.prevent.stop="
                    evh_880971572885910_clickPreventStop($event, { row })
                  "
                >
                </ItpLink>
              </ItpBox>
              <ItpDataTable
                tableId="invoiceLineMeters"
                variant="basic"
                compact
                :paging="b_1803307708030275_paging"
                :sortable="b_1803307708030275_sortable"
                :rowHeader="b_1803307708030275_rowHeader"
                use-index-as-row-key
                :fillEmptyRowSpace="b_1803307708030275_fillEmptyRowSpace"
                :value="
                  row.data.meter_accounting ? [row.data.meter_accounting] : []
                "
                :editable="!invoice.isReadOnly && !row.data.locked"
                :noCreateRow="!!row.data.meter_accounting"
                :globs="{ units, searchDataSource }"
                :messages="b_1803307708030275_messages"
                :rowTemplate="{
                  invoice_id: invoice.id,
                  invoice_line_id: row.data.id,
                  from_date: null,
                  from_amount: null,
                  from_unit: null,
                  to_date: null,
                  to_amount: null,
                  to_unit: null,
                  billing_multiplier: 1,
                }"
                :columns="b_1803307708030275_columns"
                class="bg-transparent datatable-basic mb-1"
                v-if="
                  (!row.isNew.value && row.isEditing.value) ||
                  row.data.meter_accounting
                "
                @save="evh_1803307708030275_save($event, { row })"
                @save-completed="
                  evh_1803307708030275_saveCompleted($event, { row })
                "
                @delete="evh_1803307708030275_delete($event, { row })"
                @delete-completed="
                  evh_1803307708030275_deleteCompleted($event, { row })
                "
              >
              </ItpDataTable>
              <ItpDataTable
                tableId="invoiceLineAccounting"
                variant="basic"
                compact
                :paging="b_7700024326226090_paging"
                :sortable="b_7700024326226090_sortable"
                :rowHeader="b_7700024326226090_rowHeader"
                :fillEmptyRowSpace="b_7700024326226090_fillEmptyRowSpace"
                :value="
                  row.data.accountings && row.data.accountings.data
                    ? row.data.accountings.data
                    : []
                "
                :editable="!invoice.isReadOnly && !row.data.locked"
                :rowTemplate="
                  schemaDefaults('CreateInvoiceLineAccounting', {
                    invoice_id: invoice.id,
                    invoice_line_id: row.data.id,
                  })
                "
                :globs="{ searchDataSource }"
                :messages="b_7700024326226090_messages"
                :columns="b_7700024326226090_columns"
                class="bg-transparent datatable-basic mb-1"
                v-if="
                  (row.isEditing.value && !row.isNew.value) ||
                  (row.data.accountings &&
                    row.data.accountings.data &&
                    row.data.accountings.data.length)
                "
                @save="evh_7700024326226090_save($event, { row })"
                @delete="evh_7700024326226090_delete($event, { row })"
                @save-completed="
                  evh_7700024326226090_saveCompleted($event, { row })
                "
                @delete-completed="
                  evh_7700024326226090_deleteCompleted($event, { row })
                "
              >
              </ItpDataTable>
              <ItpBox class="mb-3">
                <div class="ms-auto" v-if="hasQuantityMismatch(row)">
                  <ItpIcon
                    icon="material-symbols:warning-rounded"
                    class="text-warning"
                  >
                  </ItpIcon>
                  <ItpText
                    strong
                    :text="
                      pgettext('invoiceLineAccounting', 'Quantity mismatch.')
                    "
                  >
                  </ItpText>
                </div>
              </ItpBox>
            </ItpCol>
          </ItpRow>
        </div>
      </template>
    </ItpDataTable>
  </ItpBox>
</template>
